import { IMeasure } from "../../../shared/models/Measure";
import { IMeasureAudit } from "../../../shared/models/MeasureAudit";
import { IMeasureAuditCompany } from "../../../shared/models/MeasureAuditCompany";
import { IMeasureAuditDepartment } from "../../../shared/models/MeasureAuditDepartment";
import { IMeasureCompany } from "../../../shared/models/MeasureCompany";
import { IMeasureDepartment } from "../../../shared/models/MeasureDepartment";

const interpolate = (
  x: number,
  x1: number,
  x2: number,
  y1: number,
  y2: number
): number => {
  const slope = (y2 - y1) / (x2 - x1);
  const intercept = y1 - slope * x1;
  const y = slope * x + intercept;
  return Math.round(y * 100) / 100;
};

const calculateRating = (
  actual: number,
  thresholds: number[],
  isIncreasing: boolean
): number => {
  const ratingPairs = [
    [thresholds[0], thresholds[1], 1, 2],
    [thresholds[1], thresholds[2], 2, 3],
    [thresholds[2], thresholds[3], 3, 4],
    [thresholds[3], thresholds[4], 4, 5],
  ];

  for (const [x1, x2, y1, y2] of ratingPairs) {
    const inRange = isIncreasing
      ? actual >= x1 && actual < x2
      : actual <= x1 && actual > x2;

    if (inRange) return interpolate(actual, x1, x2, y1, y2);
  }

  // If outside the defined ranges
  if (isIncreasing) {
    return actual < thresholds[0] ? 1 : 5;
  } else {
    return actual > thresholds[0] ? 1 : 5;
  }
};

const isIncreasingType = (r1: number, r2: number, r3: number): boolean => {
  if (r1 <= r2 && r2 <= r3) return true;
  if (r1 >= r2 && r2 >= r3) return false;
  return true; // Default fallback
};

export const measureRating = (measure: IMeasure | IMeasureCompany |   IMeasureDepartment): number => {
  const actual = measure.annualActual;
  if (actual === null || actual === undefined) return 1;

  const r1 = Number(measure.rating1) || 0;
  const r2 = Number(measure.rating2) || 0;
  const r3 = Number(measure.rating3) || 0;
  const r4 = Number(measure.rating4) || 0;
  const r5 = Number(measure.rating5) || 0;

  const thresholds = [r1, r2, r3, r4, r5];
  const isIncreasing = isIncreasingType(r1, r2, r3);

  return calculateRating(actual, thresholds, isIncreasing);
};

// Returns a color representing a rating value
export const rateColor = (
  rating: number,
  isUpdated?: boolean,
  isHexValue: boolean = false
): string => {
  // Handle non-updated case first
  if (!isUpdated) {
    return isHexValue ? "#808080" : "grey";
  }

  // Color configuration matrix
  const colorRanges = [
    {
      min: 5,
      color: { name: "purple", hex: "#9b59b6" },
    },
    {
      min: 4,
      max: 5,
      color: { name: "blue", hex: "#2f80ed" },
    },
    {
      min: 3,
      max: 4,
      color: { name: "green", hex: "#4bb543" },
    },
    {
      min: 2,
      max: 3,
      color: { name: "warning", hex: "#faa05a" },
    },
    {
      min: 0,
      max: 2,
      color: { name: "red", hex: "#dc3545" },
    },
  ];

  // Find the matching color
  const matchedRange = colorRanges.find(({ min, max }) => {
    if (max === undefined) return rating >= min;
    else if (min === undefined) return rating < max;
    else return rating >= min && rating < max;
  });

  // Return color
  return matchedRange
    ? isHexValue
      ? matchedRange.color.hex
      : matchedRange.color.name
    : isHexValue
      ? "#808080"
      : "grey";
};

interface ISymobl {
  symbol: string;
  prefix?: string;
  suffix?: string;
}
export const dataTypeSymbol = (dataType: string): ISymobl => {
  if (dataType === "Percentage")
    return {
      prefix: "",
      suffix: "%",
      symbol: "%",
    };

  if (dataType === "Currency")
    return {
      prefix: "N$",
      suffix: "",
      symbol: "N$",
    };

  if (dataType === "Rating")
    return {
      prefix: "",
      suffix: "Rating",
      symbol: "Rate",
    };

  if (dataType === "Number")
    return {
      prefix: "",
      suffix: "",
      symbol: "#",
    };

  if (dataType === "Date")
    return {
      prefix: "",
      suffix: "",
      symbol: "Date",
    };

  return {
    prefix: "",
    suffix: "",
    symbol: "",
  };
};



// export const totalQ4DepartmentObjectiveRating = (
//   measures: IMeasureDepartment[] | IMeasureAuditDepartment[]
// ) => {
//   const data = measures.map((measure) => ({
//     rating: Number(measure.q4Rating) || measure.q4AutoRating || 1,
//     weight: measure.weight,
//   }));

//   let rating = data.reduce((acc, curr) => {
//     return acc + curr.rating * (curr.weight / 100);
//   }, 0);

//   let totalWeight = data.reduce((acc, curr) => {
//     return acc + curr.weight / 100;
//   }, 0);

//   rating = rating / totalWeight;
//   rating = Math.round(rating * 10) / 10;
//   return rating;
// };

export const totalQ3CompanyObjectiveRating = (
  measures: IMeasureCompany[] | IMeasureAuditCompany[]
) => {
  const data = measures.map((measure) => ({
    rating: Number(measure.q3Rating) || measure.q3AutoRating || 1,
    weight: measure.weight,
  }));

  let rating = data.reduce((acc, curr) => {
    return acc + curr.rating * (curr.weight / 100);
  }, 0);

  let totalWeight = data.reduce((acc, curr) => {
    return acc + curr.weight / 100;
  }, 0);

  rating = rating / totalWeight;
  rating = Math.round(rating * 10) / 10;
  return rating;
};

export const totalQ2CompanyObjectiveRating = (
  measures: IMeasureCompany[] | IMeasureAuditCompany[]
) => {
  const data = measures.map((measure) => ({
    rating: Number(
      measure.q2FinalRating ?? measure.q2supervisorRating ?? measure.rating
    ),
  }));

  let rating = data.reduce((acc, curr) => acc + curr.rating, 0) / data.length;
  return rating;
};
export const totalQ1CompanyObjectiveRating = (
  measures: IMeasureCompany[] | IMeasureAuditCompany[]
) => {
  const data = measures.map((measure) => ({
    rating: Number(measure.q1Rating) || measure.q1AutoRating || 1,
    weight: measure.weight,
  }));

  let rating = data.reduce((acc, curr) => {
    return acc + curr.rating * (curr.weight / 100);
  }, 0);

  let totalWeight = data.reduce((acc, curr) => {
    return acc + curr.weight / 100;
  }, 0);

  rating = rating / totalWeight;
  rating = Math.round(rating * 10) / 10;
  return rating;
};
// export const totalQ4CompanyObjectiveRating = (
//   measures: IMeasureCompany[] | IMeasureAuditCompany[]
// ) => {
//   const data = measures.map((measure) => ({
//     rating: Number(measure.q4FinalRating),
//   }));

//   let rating = data.reduce((acc, curr) => {
//     return acc + curr.rating;
//   }, 0) / data.length;

//   return rating;
// };

//rate according to the present value first.
// q4rating: number; //q2 auto rating
// q4supervisorRating: number | null; //
// q4FinalRating: number | null; //

//Might be an issue because when the supervisor is rating, the employee can already see the color changes even before approval.
//needs agreement status, maybe?
export const totalQ4CompanyObjectiveRating = (
  measures: IMeasureCompany[] | IMeasureAuditCompany[] 
) => {
  const data = measures.map((measure) => ({
    rating: Number(
      measure.q4FinalRating ?? measure.q4supervisorRating ?? measure.q4rating
    ),
  }));

  let rating = data.reduce((acc, curr) => acc + curr.rating, 0) / data.length;
  return rating;
};

// export const totalQ2IndividualObjectiveRating = (measures: IMeasure[] | IMeasureAudit[]) => {
//   const data = measures.map((measure) => ({
//     rating: Number(measure.finalRating || 0),
//   }));

//   let rating = data.reduce((acc, curr) => {
//       return acc + curr.rating;
//     }, 0) / data.length;

//   // rating = Math.round(rating * 10) / 10;
//   return rating;
// };

// autoRating: number; //employee midterm rating
// supervisorRating: number | null; //supervisor midterm rating
// finalRating: number | null;//midterm final rating

//Might be an issue because when the supervisor is rating, the employee can already see the color changes even before approval.
//needs agreement status, maybe?
export const totalQ2IndividualObjectiveRating = (
  measures: IMeasure[] | IMeasureAudit[]
) => {
  const data = measures.map((measure) => ({
    rating: Number(
      measure.finalRating ?? measure.supervisorRating ?? measure.autoRating
    ),
  }));

  let rating =
    data.reduce((acc, curr) => {
      return acc + curr.rating;
    }, 0) / data.length;
  return rating;
};

// export const totalQ4IndividualObjectiveRating = (measures: IMeasure[] | IMeasureAudit[]) => {
//   const data = measures.map((measure) => ({
//     rating: Number(measure.finalRating2 || 0),
//   }));

//   let rating = data.reduce((acc, curr) => {
//     return acc + curr.rating;
//   }, 0) / data.length;

//   // rating = Math.round(rating * 10) / 10;
//   return rating;
// };

// autoRating2: number; //employee assesssment rating
// supervisorRating2: number | null;//supervisor assesssment rating
// finalRating2: number | null;//final assesssment rating

//Might be an issue because when the supervisor is rating, the employee can already see the color changes even before approval.
//needs agreement status, maybe?
export const totalQ4IndividualObjectiveRating = (
  measures: IMeasure[] | IMeasureAudit[]
) => {
  const data = measures.map((measure) => ({
    rating: Number(
      measure.finalRating2 ?? measure.supervisorRating2 ?? measure.autoRating2
    ),
  }));

  let rating =
    data.reduce((acc, curr) => {
      return acc + curr.rating;
    }, 0) / data.length;
  return rating;
};

export const totalQ2MeasureCompanyRating = (measures: IMeasureCompany[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.q2FinalRating || 0);
    }, 0) / measures.length;
  return rating || 0;
};

export const totalQ4MeasureCompanyRating = (measures: IMeasureCompany[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.q4FinalRating || 0);
    }, 0) / measures.length;
  return rating || 0;
};

export const totalQ2MeasureRating = (measures: IMeasure[]) => {
  if (measures.length === 0) return 0

  const total = measures.reduce((acc, measure) => {
    return acc + (measure.finalRating || 0)
  }, 0)

  const rating = total / measures.length
  return isNaN(rating) ? 0 : rating
}


export const totalQ4MeasureRating = (measures: IMeasure[]) => {
  if (measures.length === 0) return 0

  const total = measures.reduce((acc, measure) => {
    return acc + (measure.finalRating2 || 0)
  }, 0)

  const rating = total / measures.length
  return isNaN(rating) ? 0 : rating
}


// Q2 company ratings
export const Q1CompanyERating = (measures: IMeasureCompany[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.rating || 0);
    }, 0) / measures.length;
  return rating;
};
export const Q1CompanySRating = (measures: IMeasureCompany[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.q2supervisorRating || 0);
    }, 0) / measures.length;
  return rating;
};

export const Q1CompanySuperRating = (measures: IMeasureCompany[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.q2FinalRating || 0);
    }, 0) / measures.length;
  return rating;
};

// Q4 Company rating
export const Q4CompanyERating = (measures: IMeasureCompany[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.q4rating || 0);
    }, 0) / measures.length;
  return rating;
};
export const Q4CompanySRating = (measures: IMeasureCompany[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.q4supervisorRating || 0);
    }, 0) / measures.length;
  return rating;
};

export const Q4CompanySuperRating = (measures: IMeasureCompany[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.q4FinalRating || 0);
    }, 0) / measures.length;
  return rating;
};

// Q2 INDIVIDUAL RATINGS
export const semester1EmpRating = (measures: IMeasure[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.autoRating || 0);
    }, 0) / measures.length;
  return rating;
};

export const semester1SuperRating = (measures: IMeasure[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.supervisorRating || 0);
    }, 0) / measures.length;
  return rating;
};
export const semester1FinalRating = (measures: IMeasure[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.finalRating || 0);
    }, 0) / measures.length;
  return rating;
};

// Q4 INDIVIVIDUAL RATINGS
export const q4EmpRating = (measures: IMeasure[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.autoRating2 || 0);
    }, 0) / measures.length;
  return rating;
};

export const q4SuperRating = (measures: IMeasure[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.supervisorRating2 || 0);
    }, 0) / measures.length;
  return rating;
};
export const q4FinalRating = (measures: IMeasure[]) => {
  const rating =
    measures.reduce((acc, measure) => {
      return acc + (measure.finalRating2 || 0);
    }, 0) / measures.length;
  return rating;
};
// DEPARTMENT RATINGS
export const totalQ1DepartmentObjectiveRating = (
  measures: IMeasureDepartment[] | IMeasureAuditDepartment[]
) => {
  const data = measures.map((measure) => ({
    rating: Number(measure.q1Rating) || measure.q1AutoRating,
    weight: measure.weight,
  }));

  let rating =
    data.reduce((acc, curr) => {
      return acc + curr.rating;
    }, 0) / data.length;

  rating = Math.round(rating * 10) / 10;
  return rating;
};
export const totalQ2DepartmentObjectiveRating = (
  measures: IMeasureDepartment[] | IMeasureAuditDepartment[]
) => {
  const data = measures.map((measure) => ({
    rating: Number(measure.q2Rating) || measure.q2AutoRating,
    weight: measure.weight,
  }));

  let rating =
    data.reduce((acc, curr) => {
      return acc + curr.rating;
    }, 0) / data.length;

  rating = Math.round(rating * 10) / 10;
  return rating;
};
export const totalQ3DepartmentObjectiveRating = (
  measures: IMeasureDepartment[] | IMeasureAuditDepartment[]
) => {
  const data = measures.map((measure) => ({
    rating: Number(measure.q3Rating) || measure.q3AutoRating,
    weight: measure.weight,
  }));

  let rating =
    data.reduce((acc, curr) => {
      return acc + curr.rating;
    }, 0) / data.length;

  rating = Math.round(rating * 10) / 10;
  return rating;
};
export const totalQ4DepartmentObjectiveRating = (
  measures: IMeasureDepartment[] | IMeasureAuditDepartment[]
) => {
  const data = measures.map((measure) => ({
    rating: Number(measure.q4Rating) || measure.q4AutoRating,
    weight: measure.weight,
  }));

  let rating =
    data.reduce((acc, curr) => {
      return acc + curr.rating;
    }, 0) / data.length;

  rating = Math.round(rating * 10) / 10;
  return rating;
};